'use client'

/**
 * Version Check Hook (Issue #3781)
 *
 * Detects when the server has deployed a new version by comparing
 * both schemaVersion AND buildTimestamp from /api/health.
 * This catches code-only deploys that don't change the schema.
 *
 * Also captures the initial buildTimestamp in sessionStorage
 * for use by resilient-submit's deployment detection.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { BUILD_TIMESTAMP_KEY } from '@/lib/submission-cache'

export type VersionStatus = 'current' | 'stale' | 'checking'

interface UseVersionCheckOptions {
  /** How often to check for version changes (default: 5 minutes) */
  checkIntervalMs?: number
  /** Enable/disable version checking (default: true) */
  enabled?: boolean
}

const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  const { checkIntervalMs = DEFAULT_CHECK_INTERVAL, enabled = true } = options

  const [status, setStatus] = useState<VersionStatus>('checking')
  const [initialVersion, setInitialVersion] = useState<string | null>(null)
  const initialVersionRef = useRef<string | null>(null)
  const initialBuildRef = useRef<string | null>(null)

  // Store version on first load
  useEffect(() => {
    if (!enabled) {
      setStatus('current')
      return
    }

    const fetchInitialVersion = async () => {
      try {
        const res = await fetch('/api/health')
        if (!res.ok) {
          setStatus('current')
          return
        }

        const data = await res.json()
        const schemaVersion = data.deployment?.schemaVersion
        const buildTimestamp = data.deployment?.buildTimestamp

        if (schemaVersion) {
          setInitialVersion(schemaVersion)
          initialVersionRef.current = schemaVersion
        }

        // Store buildTimestamp for resilient-submit deployment detection
        if (buildTimestamp) {
          initialBuildRef.current = buildTimestamp
          try {
            sessionStorage.setItem(BUILD_TIMESTAMP_KEY, buildTimestamp)
          } catch {
            // sessionStorage unavailable
          }
        }

        setStatus('current')
      } catch {
        // Network error - assume current for now
        setStatus('current')
      }
    }

    fetchInitialVersion()
  }, [enabled])

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch('/api/health')
      if (!res.ok) return

      const data = await res.json()
      const currentSchema = data.deployment?.schemaVersion
      const currentBuild = data.deployment?.buildTimestamp

      // Check both schemaVersion AND buildTimestamp
      const schemaChanged =
        currentSchema &&
        initialVersionRef.current &&
        currentSchema !== initialVersionRef.current

      const buildChanged =
        currentBuild &&
        initialBuildRef.current &&
        currentBuild !== initialBuildRef.current

      if (schemaChanged || buildChanged) {
        setStatus('stale')
      }

      // Dispatch event for submission recovery
      window.dispatchEvent(new Event('cadence:server-reachable'))
    } catch {
      // Network error - don't change status
    }
  }, [])

  // Periodic version check with visibility change listener
  useEffect(() => {
    if (!enabled || !initialVersion) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion()
      }
    }

    const interval = setInterval(checkVersion, checkIntervalMs)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [initialVersion, checkIntervalMs, enabled, checkVersion])

  // Callback to refresh the page safely
  const refresh = useCallback(() => {
    // Import safe-refresh dynamically to avoid circular deps
    import('@/lib/safe-refresh').then(({ safeRefresh }) => {
      safeRefresh()
    })
  }, [])

  return { status, refresh, triggerCheck: checkVersion }
}
